/**
 * Plain async fetchers for the transformer library, versions, preview, step
 * registry and resolver — agentic-integrations via `integrationsClient`
 * (`/api/v1/integration/transformers/**` through the gateway).
 */

import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  CreateTransformerRequest,
  PreviewRequest,
  PreviewResponse,
  ResolveParams,
  ResolveResponse,
  SaveVersionRequest,
  StepTypesResponse,
  Transformer,
  TransformerDefinition,
  TransformerConnector,
  TransformerListParams,
  TransformerSummary,
  TransformerVersion,
  UpdateTransformerRequest,
  ValidateResponse,
} from '../types/transformer.types';

/**
 * §7 says integrations answers in its `ResponseSchema` envelope `{success, message,
 * data}`. Accept a bare body too so a route that ships unwrapped still renders.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- the caller names the contract type it expects
export function unwrapEnvelope<T>(body: unknown): T {
  if (body !== null && typeof body === 'object' && 'data' in body && 'success' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/** Drops empty filters so the query string carries only what the caller set. */
function toSearchParams(params: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = String(value);
  }
  return out;
}

/** Library list, filtered by connector / format / free-text. */
export async function listTransformers(
  params: TransformerListParams = {},
): Promise<TransformerSummary[]> {
  const body = await integrationsClient
    .get(endpoints.transformers.list(), { searchParams: toSearchParams(params) })
    .json<unknown>();
  return unwrapEnvelope<TransformerSummary[]>(body);
}

/** One transformer with its current definition and version history. */
export async function getTransformer(id: string): Promise<Transformer> {
  const body = await integrationsClient.get(endpoints.transformers.detail(id)).json<unknown>();
  return unwrapEnvelope<Transformer>(body);
}

/** One saved version (for the history diff and pinned-version samples). */
export async function getTransformerVersion(
  id: string,
  version: number,
): Promise<TransformerVersion> {
  const body = await integrationsClient
    .get(endpoints.transformers.version(id, version))
    .json<unknown>();
  return unwrapEnvelope<TransformerVersion>(body);
}

/** Create a transformer (v1), blank or copied. 409 NAME_TAKEN. */
export async function createTransformer(payload: CreateTransformerRequest): Promise<Transformer> {
  const body = await integrationsClient
    .post(endpoints.transformers.list(), { json: payload })
    .json<unknown>();
  return unwrapEnvelope<Transformer>(body);
}

/** Update name / customer / description. */
export async function updateTransformer(
  id: string,
  payload: UpdateTransformerRequest,
): Promise<Transformer> {
  const body = await integrationsClient
    .patch(endpoints.transformers.detail(id), { json: payload })
    .json<unknown>();
  return unwrapEnvelope<Transformer>(body);
}

/** Save the draft as the next version. */
export async function saveTransformerVersion(
  id: string,
  payload: SaveVersionRequest,
): Promise<Transformer> {
  const body = await integrationsClient
    .post(endpoints.transformers.versions(id), { json: payload })
    .json<unknown>();
  return unwrapEnvelope<Transformer>(body);
}

/** Archive. 409 IN_USE while any assignment points at it. */
export async function archiveTransformer(id: string): Promise<void> {
  await integrationsClient.delete(endpoints.transformers.detail(id));
}

/** Validate a definition against the registry without saving it. */
export async function validateDefinition(
  connector: TransformerConnector,
  definition: TransformerDefinition,
): Promise<ValidateResponse> {
  const body = await integrationsClient
    .post(endpoints.transformers.validate(), { json: { connector, definition } })
    .json<unknown>();
  return unwrapEnvelope<ValidateResponse>(body);
}

/** Run a draft on a sample of real products through the export code path. */
export async function previewTransformer(payload: PreviewRequest): Promise<PreviewResponse> {
  const body = await integrationsClient
    .post(endpoints.transformers.preview(), { json: payload })
    .json<unknown>();
  return unwrapEnvelope<PreviewResponse>(body);
}

/** Palette + schema-driven forms for content steps, writers and file steps. */
export async function getStepTypes(): Promise<StepTypesResponse> {
  const body = await integrationsClient.get(endpoints.transformers.stepTypes()).json<unknown>();
  return unwrapEnvelope<StepTypesResponse>(body);
}

/** Which transformer(s) run for a subsidiary, connector and (optionally) SFTP connection. */
export async function resolveTransformers(params: ResolveParams): Promise<ResolveResponse> {
  const body = await integrationsClient
    .get(endpoints.transformers.resolve(), { searchParams: toSearchParams(params) })
    .json<unknown>();
  return unwrapEnvelope<ResolveResponse>(body);
}
